import { useState } from 'react';
import { Card, Group, Stack, Text, ThemeIcon, SimpleGrid, Grid, Table, Button, Paper, Center, Loader, Alert, Box, Badge, Modal } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { AreaChart } from '@mantine/charts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  IconCash, IconWallet, IconCoins, IconBuildingBank, IconArrowUpRight, IconArrowDownRight, IconRefresh,
  IconCalendar, IconListDetails, IconAlertTriangle, IconChartPie, IconBulb, IconInfoCircle, IconCircleCheck,
  IconChartArea, IconCalendarCog, IconPlus, type Icon,
} from '@tabler/icons-react';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { ExpenseFormModal } from './ExpenseFormModal';
import { formatSom, formatSomShort, tzDateKey } from '../lib/format';
import { monthLabel } from '../lib/month';

/** Naqd / bank kesimidagi summa (backend `/reports/cashbook` bilan bir xil shakl). */
interface Split { cash: number; bank: number; total: number }
interface ExpType { name: string | null; category: string; amount: number }
interface Cashbook {
  date: string;
  prevDate: string;
  /** Kassa hisobi boshlangan kun (YYYY-MM-DD); undan oldingi yozuvlar hisobga olinmaydi. null — butun tarix. */
  startDate: string | null;
  income: Split & { count: { cash: number; bank: number; reversals: number; reversalsBy?: { cash: number; bank: number } } };
  expense: Split & { count: { cash: number; bank: number }; topTypes: { cash: ExpType[]; bank: ExpType[] } };
  net: Split;
  prev: { income: number; expense: number };
  balance: Split;
  chart: { year: string; months: { month: string; income: number; expense: number }[] };
  debt: {
    month: string;
    fromMonth: string;
    total: number;
    count: number;
    items: { studentId: string; fullName: string; className: string | null; debt: number; months: number }[];
  };
}

/** Summa — so'msiz (jadval ustuni sarlavhasida "so'm" yozilgan). */
const num = (v: number) => v.toLocaleString('ru-RU').replace(/,/g, ' ');
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** Rangli fonli karta — maketdagi kirim (yashil), chiqim (qizil), qoldiq (ko'k), kassa (binafsha). */
function Tinted({ color, children, h }: { color: string; children: React.ReactNode; h?: string | number }) {
  return (
    <Card
      padding="lg"
      h={h}
      style={{ background: `var(--mantine-color-${color}-light)`, borderColor: `var(--mantine-color-${color}-light-hover)` }}
    >
      {children}
    </Card>
  );
}

/** Yuqoridagi 4 ta asosiy kartaning bittasi. */
function KpiCard({ color, icon: IconCmp, label, value, unit, foot }: { color: string; icon: Icon; label: string; value: number; unit: string; foot: React.ReactNode }) {
  return (
    <Tinted color={color} h="100%">
      <Group align="flex-start" wrap="nowrap" gap="md">
        <ThemeIcon size={52} radius="md" color={color} variant="filled"><IconCmp size={28} stroke={1.7} /></ThemeIcon>
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text fw={700} c={`var(--mantine-color-${color}-light-color)`}>{label}</Text>
          <Text fz={24} fw={800} lh={1.2}>
            <span style={{ whiteSpace: 'nowrap' }}>{num(value)}</span>{' '}
            <Text span fz={15} fw={700}>{unit}</Text>
          </Text>
          {foot}
        </Stack>
      </Group>
    </Tinted>
  );
}

/** Naqd yoki bank: shu kunning kirim / chiqim / qoldig'i — uchta katakcha. */
function BoxCard({ color, icon: IconCmp, title, sub, split, labels, foot }: {
  color: string; icon: Icon; title: string; sub: string;
  split: { income: number; expense: number };
  labels: { income: string; expense: string; rest: string };
  foot: string;
}) {
  const rest = split.income - split.expense;
  const cells = [
    { label: labels.income, value: split.income, c: undefined },
    { label: labels.expense, value: split.expense, c: undefined },
    { label: labels.rest, value: rest, c: rest < 0 ? 'red.7' : color === 'green' ? 'green.7' : `${color}.7` },
  ];
  return (
    <Card padding="md" h="100%" style={{ borderColor: `var(--mantine-color-${color}-light-hover)`, display: 'flex', flexDirection: 'column' }}>
      <Group gap="sm" mb="sm" wrap="nowrap">
        <ThemeIcon size={42} radius="md" color={color} variant="filled"><IconCmp size={24} stroke={1.7} /></ThemeIcon>
        <Stack gap={0}>
          <Text fw={700} size="lg">{title}</Text>
          <Text size="xs" c="dimmed">{sub}</Text>
        </Stack>
      </Group>
      <SimpleGrid cols={3} spacing={6} style={{ flex: 1 }}>
        {cells.map((x) => (
          <Paper key={x.label} withBorder radius="md" px="xs" py="md" ta="center"
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Text size="xs" c="dimmed" mb={6}>{x.label}</Text>
            <Text size="sm" fw={700} c={x.c} style={{ whiteSpace: 'nowrap' }}>{num(x.value)}</Text>
          </Paper>
        ))}
      </SimpleGrid>
      <Text size="xs" c="dimmed" mt="sm">{foot}</Text>
    </Card>
  );
}

/** Kun oxiriga yig'ilgan qoldiq (naqd yoki bank). */
function LeftTile({ color, icon: IconCmp, label, hint, value }: { color: string; icon: Icon; label: string; hint: string; value: number }) {
  return (
    <Tinted color={color}>
      <Group gap={8} wrap="nowrap" mb={6}>
        <ThemeIcon size={30} radius="md" color={color} variant="light"><IconCmp size={18} stroke={1.7} /></ThemeIcon>
        <Text size="sm" fw={600}>{label}</Text>
      </Group>
      <Text fz={19} fw={800} c={value < 0 ? 'red.7' : undefined} style={{ whiteSpace: 'nowrap' }}>{formatSom(value)}</Text>
      <Text size="xs" c="dimmed">{hint}</Text>
    </Tinted>
  );
}

/**
 * KASSA BOSHLANISH SANASI — tizim ishga tushirilgan kun. Undan oldin kiritilgan (tarixiy)
 * to'lov va xarajatlar kassa hisobotiga kirmaydi, lekin o'chirilmaydi: o'quvchi balansi
 * va qarzdorlik ular bilan hisoblanadi.
 */
function StartDateModal({ opened, onClose, current }: { opened: boolean; onClose: () => void; current: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [value, setValue] = useState<Date | null>(null);
  const save = useMutation({
    mutationFn: (startDate: string | null) => api.put('/reports/cashbook/start', { startDate }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('cashbook.startSaved') });
      qc.invalidateQueries({ queryKey: ['cashbook'] });
      onClose();
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });
  const picked = value ?? (current ? dayjs(current).toDate() : new Date());

  return (
    <Modal opened={opened} onClose={onClose} title={t('cashbook.startTitle')} onExitTransitionEnd={() => setValue(null)}>
      <Stack gap="sm">
        <Text size="sm">{t('cashbook.startDesc')}</Text>
        <DatePickerInput
          label={t('cashbook.startLabel')}
          value={picked}
          onChange={setValue}
          valueFormat="DD.MM.YYYY"
          maxDate={new Date()}
          leftSection={<IconCalendar size={18} />}
        />
        <Group justify="space-between" mt="xs">
          {current ? (
            <Button variant="subtle" color="gray" loading={save.isPending} onClick={() => save.mutate(null)}>
              {t('cashbook.startClear')}
            </Button>
          ) : <span />}
          <Button loading={save.isPending} onClick={() => save.mutate(dayjs(picked).format('YYYY-MM-DD'))}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * KASSA HISOBOTI — Moliya sahifasining birinchi ko'rinishi.
 * Ma'lumot bitta so'rovda: `GET /reports/cashbook?date=` (hisob qoidalari backend'da, izohlari o'sha yerda).
 */
export function CashbookPanel({ onShowDebtors }: { onShowDebtors?: () => void }) {
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const monthFull = t('monthsFull', { returnObjects: true }) as string[];
  const [date, setDate] = useState<Date>(() => new Date());
  const [startOpen, setStartOpen] = useState(false);
  const key = dayjs(date).format('YYYY-MM-DD');
  const [xarajatOchiq, setXarajatOchiq] = useState(false);
  // O'tgan kun tanlangan bo'lsa xarajat O'SHA kunga yoziladi (kun o'rtasi, Toshkent —
  // server kunni Toshkent bo'yicha ajratadi); bugun bo'lsa server vaqtni o'zi qo'yadi.
  const xarajatSanasi = key === tzDateKey(new Date()) ? undefined : `${key}T12:00:00+05:00`;

  const { data, isLoading, isError, refetch, isFetching } = useQuery<Cashbook>({
    queryKey: ['cashbook', key],
    queryFn: async () => (await api.get<Cashbook>('/reports/cashbook', { params: { date: key } })).data,
  });

  const dateLabel = dayjs(date).format('DD.MM.YYYY');

  /** O'tgan kun bilan solishtirish: kirim o'sishi — yaxshi (yashil), chiqim o'sishi — qizil. */
  const delta = (cur: number, prev: number, upIsGood: boolean) => {
    if (prev <= 0) return <Text size="sm" c="dimmed">{t('cashbook.prevZero')}</Text>;
    const p = Math.round(((cur - prev) / prev) * 100);
    if (p === 0) return <Text size="sm" c="dimmed">{t('cashbook.same')}</Text>;
    const up = p > 0;
    const good = up === upIsGood;
    const Arrow = up ? IconArrowUpRight : IconArrowDownRight;
    return (
      <Group gap={4} wrap="nowrap">
        <Arrow size={16} color={`var(--mantine-color-${good ? 'green' : 'red'}-7)`} />
        <Text size="sm" c={`var(--mantine-color-${good ? 'green' : 'red'}-light-color)`}>{t('cashbook.vsPrev', { pct: Math.abs(p) })}</Text>
      </Group>
    );
  };

  const header = (
    <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
      <Group gap="sm" wrap="wrap">
        <DatePickerInput
          aria-label={t('cashbook.date')}
          value={date}
          onChange={(d) => d && setDate(d)}
          valueFormat="DD.MM.YYYY"
          minDate={data?.startDate ? dayjs(data.startDate).toDate() : undefined}
          maxDate={new Date()}
          leftSection={<IconCalendar size={18} />}
          w={170}
        />
        <Text c="dimmed" size="sm">{t('cashbook.subtitle')}</Text>
        {data?.startDate && (
          <Badge variant="light" color="violet">{t('cashbook.startFrom', { date: dayjs(data.startDate).format('DD.MM.YYYY') })}</Badge>
        )}
      </Group>
      <Group gap="sm">
        <Button leftSection={<IconCalendarCog size={18} />} variant="subtle" onClick={() => setStartOpen(true)}>
          {t('cashbook.startButton')}
        </Button>
        <Button leftSection={<IconRefresh size={18} />} variant="light" loading={isFetching} onClick={() => refetch()}>
          {t('cashbook.refresh')}
        </Button>
        <Button leftSection={<IconPlus size={18} />} onClick={() => setXarajatOchiq(true)}>
          {t('expenses.add')}
        </Button>
      </Group>
      <StartDateModal opened={startOpen} onClose={() => setStartOpen(false)} current={data?.startDate ?? null} />
      {/* Xarajatlar sahifasidagi bilan AYNI oyna; saqlangach kassa so'rovi yangilanadi */}
      <ExpenseFormModal
        opened={xarajatOchiq}
        onClose={() => setXarajatOchiq(false)}
        forMonth={key.slice(0, 7)}
        spentAt={xarajatSanasi}
      />
    </Group>
  );

  if (isLoading) return <>{header}<Center py={80}><Loader /></Center></>;
  if (isError || !data) {
    return (
      <>
        {header}
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>{t('common.error')}</Alert>
      </>
    );
  }

  const { income, expense, net, balance, prev, debt } = data;
  const typeNames = (list: ExpType[]) =>
    list.length ? list.map((x) => x.name ?? t(`expenseCat.${x.category}`)).join(', ') : '—';
  const incomeNote = (count: number, reversals: number) =>
    [count ? t('cashbook.payCount', { count }) : null, reversals ? t('cashbook.reversals', { count: reversals }) : null]
      .filter(Boolean).join(', ') || '—';

  const rows = [
    { type: t('cashbook.incCash'), sum: income.cash, color: 'green.7', note: incomeNote(income.count.cash, income.count.reversalsBy?.cash ?? 0) },
    { type: t('cashbook.incBank'), sum: income.bank, color: 'green.7', note: incomeNote(income.count.bank, income.count.reversalsBy?.bank ?? 0) },
    { type: t('cashbook.expCash'), sum: expense.cash, color: 'red.7', note: typeNames(expense.topTypes.cash) },
    { type: t('cashbook.expBank'), sum: expense.bank, color: 'red.7', note: typeNames(expense.topTypes.bank) },
  ];

  const shares = [
    { label: t('cashbook.cashIn'), value: income.cash, hint: t('cashbook.shareHintIn', { pct: pct(income.cash, income.total) }), color: 'green', icon: IconCash },
    { label: t('cashbook.cashOut'), value: expense.cash, hint: t('cashbook.shareHintOut', { pct: pct(expense.cash, expense.total) }), color: 'red', icon: IconWallet },
    { label: t('cashbook.bankIn'), value: income.bank, hint: t('cashbook.shareHintIn', { pct: pct(income.bank, income.total) }), color: 'blue', icon: IconBuildingBank },
    { label: t('cashbook.bankOut'), value: expense.bank, hint: t('cashbook.shareHintOut', { pct: pct(expense.bank, expense.total) }), color: 'violet', icon: IconBuildingBank },
  ];

  const summary = [
    t('cashbook.sumIncome', { sum: formatSom(income.total) }),
    t('cashbook.sumExpense', { sum: formatSom(expense.total) }),
    t('cashbook.sumNet', { sum: formatSom(net.total) }),
    t('cashbook.sumCash', { sum: formatSom(balance.total), cash: formatSom(balance.cash), bank: formatSom(balance.bank) }),
    t('cashbook.sumDebt', { sum: formatSom(debt.total), count: debt.count }),
  ];

  const debtMonth = monthLabel(debt.month, monthFull);
  const som = t('currency.som');
  const boxFoot = (pays: number, exps: number) =>
    `${t('cashbook.payCount', { count: pays })} · ${t('cashbook.expCount', { count: exps })}`;

  return (
    <>
      {header}

      {/* 1. Asosiy ko'rsatkichlar */}
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} mb="md">
        <KpiCard color="green" icon={IconCash} label={t('cashbook.dayIncome')} value={income.total} unit={som} foot={delta(income.total, prev.income, true)} />
        <KpiCard color="red" icon={IconWallet} label={t('cashbook.dayExpense')} value={expense.total} unit={som} foot={delta(expense.total, prev.expense, false)} />
        <KpiCard color="blue" icon={IconCoins} label={t('cashbook.dayNet')} value={net.total} unit={som} foot={<Text size="sm" c="var(--mantine-color-blue-light-color)">{t('cashbook.netHint')}</Text>} />
        <KpiCard color="violet" icon={IconBuildingBank} label={t('cashbook.totalCash')} value={balance.total} unit={som} foot={<Text size="sm" c="var(--mantine-color-violet-light-color)">{t('cashbook.totalHint')}</Text>} />
      </SimpleGrid>

      {/* 2. Naqd / bank, qoldiqlar va grafik */}
      <Grid mb="md" gutter="md">
        <Grid.Col span={{ base: 12, md: 6, xl: 3 }}>
          <BoxCard
            color="green" icon={IconCash} title={t('cashbook.cashBox')} sub={`${t('cashbook.shuKun')} · ${dateLabel}`}
            split={{ income: income.cash, expense: expense.cash }}
            labels={{ income: t('cashbook.income'), expense: t('cashbook.expense'), rest: t('cashbook.rest') }}
            foot={boxFoot(income.count.cash, expense.count.cash)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, xl: 3 }}>
          <BoxCard
            color="blue" icon={IconBuildingBank} title={t('cashbook.bank')} sub={`${t('cashbook.bankSub')} · ${t('cashbook.shuKun')}`}
            split={{ income: income.bank, expense: expense.bank }}
            labels={{ income: t('cashbook.income'), expense: t('cashbook.expense'), rest: t('cashbook.rest') }}
            foot={boxFoot(income.count.bank, expense.count.bank)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5, xl: 2 }}>
          <Stack gap="md" h="100%" justify="space-between">
            <LeftTile color="green" icon={IconCash} label={t('cashbook.cashLeft')} hint={t('cashbook.leftHint')} value={balance.cash} />
            <LeftTile color="blue" icon={IconBuildingBank} label={t('cashbook.bankLeft')} hint={t('cashbook.leftHint')} value={balance.bank} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7, xl: 4 }}>
          <SectionCard title={t('cashbook.chart', { year: data.chart.year })} icon={IconChartArea} color="blue" h="100%" p="sm">
            <AreaChart
              h={190}
              data={data.chart.months.map((m) => ({ oy: monthNames[Number(m.month.slice(5)) - 1] ?? m.month.slice(5), income: m.income, expense: m.expense }))}
              dataKey="oy"
              withLegend
              legendProps={{ verticalAlign: 'top', height: 30 }}
              series={[
                { name: 'income', color: 'green.6', label: t('cashbook.income') },
                { name: 'expense', color: 'red.6', label: t('cashbook.expense') },
              ]}
              curveType="monotone"
              withGradient
              withDots={false}
              valueFormatter={(v) => formatSom(v)}
              yAxisProps={{ width: 54, tickFormatter: (v: number) => formatSomShort(v) }}
            />
          </SectionCard>
        </Grid.Col>
      </Grid>

      {/* 3. Kun tafsilotlari va qarzdorlar */}
      <Grid mb="md" gutter="md">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <SectionCard title={t('cashbook.details')} subtitle={dateLabel} icon={IconListDetails} color="blue" p={0} h="100%">
            <Table.ScrollContainer minWidth={560}>
              <Table verticalSpacing="sm" horizontalSpacing="md" striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>№</Table.Th>
                    <Table.Th>{t('cashbook.colType')}</Table.Th>
                    <Table.Th ta="right">{t('cashbook.colSum')}</Table.Th>
                    <Table.Th>{t('cashbook.colDate')}</Table.Th>
                    <Table.Th>{t('cashbook.colNote')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((r, i) => (
                    <Table.Tr key={r.type}>
                      <Table.Td>{i + 1}</Table.Td>
                      <Table.Td>{r.type}</Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={600} c={r.sum ? r.color : 'dimmed'}>{num(r.sum)}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{dateLabel}</Text></Table.Td>
                      <Table.Td><Text size="sm" lineClamp={1}>{r.note}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr style={{ background: 'var(--mantine-color-default-hover)' }}>
                    <Table.Td />
                    <Table.Td><Text fw={700}>{t('cashbook.total')}</Text></Table.Td>
                    <Table.Td ta="right">
                      <Text fw={800} c="green.7">{num(income.total)}</Text>
                      <Text fw={800} c="red.7">−{num(expense.total)}</Text>
                    </Table.Td>
                    <Table.Td><Text size="sm" fw={600}>{t('cashbook.rest')}</Text></Table.Td>
                    <Table.Td><Text fw={800} c={net.total < 0 ? 'red.7' : 'blue.7'}>{formatSom(net.total)}</Text></Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </SectionCard>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <SectionCard
            title={t('cashbook.debtTitle', { month: debtMonth })}
            icon={IconAlertTriangle}
            color="red"
            p={0}
            h="100%"
            right={onShowDebtors && debt.count > 0 ? (
              <Button size="xs" variant="subtle" color="red" onClick={onShowDebtors}>{t('cashbook.seeAll')}</Button>
            ) : undefined}
          >
            {debt.count === 0 ? (
              <Center py="xl"><Text c="dimmed" size="sm">{t('cashbook.debtNone')}</Text></Center>
            ) : (
              <>
                <Table.ScrollContainer minWidth={420}>
                  <Table verticalSpacing={8} horizontalSpacing="sm" striped>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={36}>№</Table.Th>
                        <Table.Th>{t('cashbook.colStudent')}</Table.Th>
                        <Table.Th>{t('cashbook.colClass')}</Table.Th>
                        <Table.Th ta="right">{t('cashbook.colDebt')}</Table.Th>
                        <Table.Th>{t('cashbook.colNote')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {debt.items.map((d, i) => (
                        <Table.Tr key={d.studentId}>
                          <Table.Td>{i + 1}</Table.Td>
                          <Table.Td><Text size="sm" lineClamp={1}>{d.fullName}</Text></Table.Td>
                          <Table.Td>{d.className ?? '—'}</Table.Td>
                          <Table.Td ta="right"><Text size="sm" fw={600} c="red.7">{num(d.debt)}</Text></Table.Td>
                          <Table.Td><Text size="sm">{t('cashbook.monthsDebt', { count: d.months })}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
                <Box p="sm">
                  <Tinted color="red">
                    <Group justify="space-between" wrap="wrap" gap="xs">
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon color="red" radius="xl" size={34}><IconAlertTriangle size={18} /></ThemeIcon>
                        <Stack gap={0}>
                          <Text fw={700} c="var(--mantine-color-red-light-color)">{t('cashbook.debtTotal')}</Text>
                          <Text size="xs" c="dimmed">{t('cashbook.debtCount', { count: debt.count })}</Text>
                        </Stack>
                      </Group>
                      <Text fz={20} fw={800} c="var(--mantine-color-red-light-color)">{formatSom(debt.total)}</Text>
                    </Group>
                  </Tinted>
                  <Text size="xs" c="dimmed" mt={6}>{t('cashbook.monthsHint', { from: monthLabel(debt.fromMonth, monthFull) })}</Text>
                </Box>
              </>
            )}
          </SectionCard>
        </Grid.Col>
      </Grid>

      {/* 4. Ulushlar va xulosa */}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <SectionCard title={t('cashbook.shareTitle')} icon={IconChartPie} color="yellow" h="100%">
            <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
              {shares.map((s) => (
                <Tinted key={s.label} color={s.color}>
                  <Group gap={6} mb={6} wrap="nowrap">
                    <ThemeIcon size={24} radius="sm" color={s.color} variant="light"><s.icon size={15} /></ThemeIcon>
                    <Text size="sm" fw={600}>{s.label}</Text>
                  </Group>
                  <Text fz={20} fw={800} c={`var(--mantine-color-${s.color}-light-color)`} ta="center" style={{ whiteSpace: 'nowrap' }}>{num(s.value)}</Text>
                  <Text size="xs" c="dimmed" ta="center">{s.hint}</Text>
                </Tinted>
              ))}
            </SimpleGrid>
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <SectionCard title={t('cashbook.summary')} icon={IconBulb} color="yellow" h="100%">
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap={6}>
                  {summary.map((line) => (
                    <Group key={line} gap={6} wrap="nowrap" align="flex-start">
                      <IconCircleCheck size={18} color="var(--mantine-color-green-6)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <Text size="sm">{line}</Text>
                    </Group>
                  ))}
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Tinted color="blue">
                  <Group gap={6} mb={6} wrap="nowrap">
                    <IconInfoCircle size={18} color="var(--mantine-color-blue-7)" />
                    <Text size="sm" fw={700}>{t('cashbook.howTitle')}</Text>
                  </Group>
                  <Stack gap={4}>
                    {(['how1', 'how2', 'how3', 'how4'] as const).map((k) => (
                      <Text key={k} size="xs">{t(`cashbook.${k}`)}</Text>
                    ))}
                  </Stack>
                </Tinted>
              </Grid.Col>
            </Grid>
          </SectionCard>
        </Grid.Col>
      </Grid>
    </>
  );
}
